import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link,
  Checkbox,
  FormGroup
} from '@mui/material'
import {
  CheckCircle,
  Error as ErrorIcon,
  Info,
  OpenInNew
} from '@mui/icons-material'
import { IntegrationsService, ZapierEvent } from '../../../../shared/services/IntegrationsService'

export default function ZapierIntegration() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [availableEvents, setAvailableEvents] = useState<ZapierEvent[]>([])
  const [isConfigured, setIsConfigured] = useState(false)
  const [lastTriggered, setLastTriggered] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    loadZapierSettings()
  }, [])

  const loadZapierSettings = async () => {
    try {
      setLoading(true)

      // Load available events
      const events = await IntegrationsService.getZapierEvents()
      setAvailableEvents(events)

      // Load current settings
      const settings = await IntegrationsService.getZapierIntegration()
      setEnabled(settings.enabled)
      setWebhookUrl(settings.webhookUrl || '')
      setSelectedEvents(settings.events || [])
      setIsConfigured(settings.isConfigured)
      setLastTriggered(settings.lastTriggered || null)
    } catch (error: any) {
      console.error('Failed to load Zapier settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEventToggle = (eventKey: string) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventKey)) {
        return prev.filter(e => e !== eventKey)
      } else {
        return [...prev, eventKey]
      }
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setTestResult(null)

      // Validate webhook URL if provided
      if (webhookUrl.trim() && !webhookUrl.startsWith('https://hooks.zapier.com')) {
        setSaveError('Invalid webhook URL. Must be a Zapier webhook URL (https://hooks.zapier.com)')
        return
      }

      await IntegrationsService.saveZapierIntegration({
        enabled,
        webhookUrl: webhookUrl.trim() || undefined,
        events: selectedEvents.length > 0 ? selectedEvents : undefined
      })

      setIsConfigured(!!webhookUrl.trim())

      // Show success message briefly
      setTimeout(() => {
        setTestResult({ success: true, message: 'Zapier integration settings saved successfully' })
        setTimeout(() => setTestResult(null), 3000)
      }, 100)
    } catch (error: any) {
      setSaveError(error.response?.data?.message || 'Failed to save Zapier integration settings')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!webhookUrl.trim()) {
      setTestResult({ success: false, message: 'Please enter a webhook URL first' })
      return
    }

    if (!webhookUrl.startsWith('https://hooks.zapier.com')) {
      setTestResult({ success: false, message: 'Invalid webhook URL. Must be a Zapier webhook URL (https://hooks.zapier.com)' })
      return
    }

    try {
      setTesting(true)
      setTestResult(null)

      const result = await IntegrationsService.testZapierWebhook({
        webhookUrl: webhookUrl.trim()
      })

      setTestResult(result)
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Failed to test webhook. Please check your URL.'
      })
    } finally {
      setTesting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Zapier integration?')) {
      return
    }

    try {
      setSaving(true)
      await IntegrationsService.disconnectZapierIntegration()

      setEnabled(false)
      setWebhookUrl('')
      setSelectedEvents([])
      setIsConfigured(false)
      setLastTriggered(null)
      setTestResult(null)

      setTestResult({ success: true, message: 'Zapier integration disconnected successfully' })
      setTimeout(() => setTestResult(null), 3000)
    } catch (error: any) {
      setSaveError('Failed to disconnect Zapier integration')
    } finally {
      setSaving(false)
    }
  }

  const formatLastTriggered = (dateString: string | null) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
        Zapier Integration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your helpdesk to 6,000+ apps with Zapier. Automate workflows and send ticket notifications anywhere.
      </Typography>

      {/* Info Card */}
      <Card sx={{ mb: 3, bgcolor: 'info.lighter' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Info color="info" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                How it works
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create a Zap in Zapier using the "Webhooks by Zapier" trigger.
                We'll send ticket events to your webhook URL, which you can then route to any of Zapier's 6,000+ apps.
              </Typography>
              <Link
                href="https://zapier.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mr: 2 }}
              >
                Visit Zapier
                <OpenInNew fontSize="small" />
              </Link>
              <Link
                href="/admin/docs/zapier-integration"
                target="_blank"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                View Setup Guide
                <OpenInNew fontSize="small" />
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enable Zapier Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  {enabled ? 'Active - Notifications will be sent to Zapier' : 'Inactive - No notifications will be sent'}
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Zapier Webhook URL"
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            helperText="Paste your Zapier webhook URL here. See setup guide for instructions."
            disabled={!enabled}
          />
        </Grid>

        {/* Event Selection */}
        {enabled && webhookUrl.trim() && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Select Events to Send
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Choose which ticket events should trigger your Zapier webhook
            </Typography>
            <FormGroup>
              {availableEvents.map(event => (
                <FormControlLabel
                  key={event.key}
                  control={
                    <Checkbox
                      checked={selectedEvents.includes(event.key)}
                      onChange={() => handleEventToggle(event.key)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {event.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || testing || !webhookUrl.trim()}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={saving || testing || !webhookUrl.trim()}
            >
              {testing ? <CircularProgress size={20} /> : 'Test Connection'}
            </Button>

            {isConfigured && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={saving || testing}
              >
                Disconnect
              </Button>
            )}
          </Box>
        </Grid>

        {/* Test Result */}
        {testResult && (
          <Grid item xs={12}>
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
            >
              {testResult.message}
            </Alert>
          </Grid>
        )}

        {/* Save Error */}
        {saveError && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Grid>
        )}

        {/* Connection Status */}
        {isConfigured && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: enabled ? 'success.lighter' : 'grey.100' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {enabled ? (
                    <CheckCircle color="success" />
                  ) : (
                    <Info color="disabled" />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {enabled ? 'Connected and Active' : 'Connected but Inactive'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {enabled
                        ? `${selectedEvents.length} event${selectedEvents.length !== 1 ? 's' : ''} configured`
                        : 'Enable notifications above to start sending updates'}
                    </Typography>
                    {lastTriggered && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Last triggered: {formatLastTriggered(lastTriggered)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
